import React, { useState } from "react";

import { useCreateInterface, useInterfaces, useWhatsappLink } from "@/shared/api/interfaces";
import { GhostButton, PrimaryButton } from "@/shared/components/settings-atoms";
import { catalogFor } from "@/shared/lib/interface-catalog";
import { FormError } from "./FormError";
import { InterfaceDialog } from "./InterfaceDialog";
import { LinkedAppsWarning } from "./LinkedAppsWarning";
import { WhatsappLink } from "./WhatsappLink";

import type { SetupProps } from "../types";

/** WhatsApp's answers, in this dialog's words. */
const WORDS = {
  3110005: "Tick the box first — linking a number needs your go-ahead.",
  3110003: "Couldn't reach WhatsApp — try again.",
};

const titleFor = (existing: SetupProps["existing"]) =>
  !existing ? "Connect WhatsApp" : existing.label ? `Link ${existing.label} again` : "Finish connecting WhatsApp";

/**
 * Link a WhatsApp number. Consent comes first, because the connection is
 * unofficial and it is the customer's number at risk; then the code. A row
 * that still needs setup, or was logged out, opens straight at step two — at
 * the code if its session is still running, else at "Show a new code".
 *
 * The link is polled only while this dialog is open — closing it drops the
 * query, which is what stops the poll.
 */
export function WhatsappSetup({ agent, existing, onClose }: SetupProps) {
  const create = useCreateInterface();
  const [accepted, setAccepted] = useState(false);
  const [started, setStarted] = useState(existing);
  const link = useWhatsappLink(started?.id ?? null);
  const { data: rows } = useInterfaces(agent.id);
  const meta = catalogFor("whatsapp")!;
  // The freshest copy of the row: when a session ends, its error says why.
  const row = rows?.find((r) => r.id === started?.id) ?? started;

  const start = async () => {
    try {
      setStarted(await create.send({ agent_id: agent.id, type: "whatsapp", accept_unofficial: true }));
    } catch {
      // FormError shows it under the box.
    }
  };

  const footer = !started ? (
    <>
      <GhostButton onClick={onClose}>Cancel</GhostButton>
      <PrimaryButton onClick={start} disabled={!accepted || create.isPending}>
        {create.isPending ? "Starting…" : "Continue"}
      </PrimaryButton>
    </>
  ) : link.data?.state === "linked" ? (
    <PrimaryButton onClick={onClose}>Done</PrimaryButton>
  ) : (
    <GhostButton onClick={onClose}>Close</GhostButton>
  );

  return (
    <InterfaceDialog
      meta={meta}
      title={titleFor(existing)}
      // The body says where linking stands — why it ended included — so the header does not repeat it.
      description={meta.desc}
      onClose={onClose}
      footer={footer}
    >
      {row ? (
        <WhatsappLink interfaceId={row.id} link={link} ended={row.error} agentName={agent.name} />
      ) : (
        <>
          <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
            {agent.name} answers from a number you link the way WhatsApp Web is linked: by scanning a code with the
            phone that owns it.
          </p>
          <label className="flex items-start gap-2.5 rounded-[10px] bg-[var(--warn-bg)] text-[var(--warn-text)] ring-1 ring-[var(--warn-ring)] px-3 py-2 text-[12.5px] leading-[1.5] cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 shrink-0 accent-[var(--text-primary)]"
            />
            <span>
              This uses an unofficial WhatsApp Web connection. WhatsApp may ban the number. Use a dedicated number,
              not your personal one.
            </span>
          </label>
          <FormError error={create.error} words={WORDS} />
          <LinkedAppsWarning agent={agent} />
        </>
      )}
    </InterfaceDialog>
  );
}
